/**
 * Sesión del usuario, sus permisos y el ciclo de login/logout.
 *
 * Es el único store con `persist: true`: el usuario y sus permisos sobreviven a
 * la recarga de página, lo que evita pedir `/api/me` en cada navegación.
 *
 * ── Orden de registro de los handlers HTTP ───────────────────────
 *
 * `_registrarHandlersHttp()` se llama SOLO tras un login exitoso. Durante
 * `restaurarSesion()` los handlers están en null a propósito, para que ese flujo
 * maneje sus propios errores sin desencadenar un logout en cascada al recargar.
 *
 * ── Sobre los permisos ───────────────────────────────────────────
 *
 * `tienePermiso()` sirve para OCULTAR acciones que producirían un 403, no para
 * autorizar: la autorización real la aplica el backend en cada petición.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia';
import { loginService } from '@/services/LoginService';
import { loginConGoogle, getFirebaseIdToken, refreshFirebaseIdToken, logoutFirebase } from '@/services/LoginFirebaseService';
import { http, setAuthToken, registrarHandlersAuth, limpiarHandlersAuth } from '@/services/http';
import type { Usuario } from '@/interfaces/Usuario';

function extraerPermisos(usuario: Usuario | null): string[] {
  return usuario?.role?.privilegios?.map(p => p.nombre) ?? []
}

/** Store `autenticar`. Ver la cabecera del archivo para el contrato del módulo. */
export const useAutenticar = defineStore('autenticar', {
    state: () => ({
        autenticado: false as boolean,
        urlAutenticar: `${process.env.VUE_APP_API_BASE_URL}/api/login`,
        correoUsuario: null as string | null,
        usuarioActual: null as Usuario | null,
        permisos: [] as string[],
    }),
    persist: true,
    getters: {
        tienePermiso: (state) => (clave: string): boolean => {
            return state.permisos.includes(clave)
        },
        esAdmin: (state): boolean => {
            return state.usuarioActual?.role?.nombre === 'Admin'
        },
    },
    actions: {
        // Registra los handlers de refresco y logout en el cliente HTTP.
        // Se llama solo tras auth exitoso; durante restaurarSesion() los handlers
        // están en null para que ese flujo maneje sus propios errores.
        _registrarHandlersHttp() {
            registrarHandlersAuth(
                async () => {
                    const token = await refreshFirebaseIdToken()
                    if (token) setAuthToken(token)
                    return token
                },
                async () => {
                    await this.Logout()
                    window.location.replace('/Ingreso')
                }
            )
        },

        async login(correo: string, contraseña: string): Promise<boolean> {
            try {
                const resultado = await loginService(correo, contraseña, this.urlAutenticar);
                this.autenticado = resultado;
                return resultado;
            } catch (error) {
                console.error('Login failed:', error);
                this.autenticado = false;
                return false;
            }
        },

        async loginGoogle(): Promise<{ ok: boolean; error?: string }> {
            try {
                const user = await loginConGoogle();
                const token = await user.getIdToken();
                setAuthToken(token);

                const result = await http.get<Usuario>('/api/me');
                if (!result.ok) {
                    await logoutFirebase();
                    setAuthToken(null);
                    const msg = result.status === 403
                        ? 'Tu cuenta está inactiva. Contacta al administrador.'
                        : result.status === 404
                            ? 'Tu usuario no está dado de alta en el sistema. Contacta al administrador.'
                            : result.status === 0
                                ? 'No se pudo conectar al servidor. Verifique su conexión e intente de nuevo.'
                                : `Error del servidor (HTTP ${result.status}). Intente de nuevo o contacte al administrador.`;
                    return { ok: false, error: msg };
                }

                this.autenticado = true;
                this.correoUsuario = user.email;
                this.usuarioActual = result.data ?? null;
                this.permisos = extraerPermisos(result.data ?? null);
                this._registrarHandlersHttp();
                return { ok: true };
            } catch (error) {
                this.autenticado = false;
                this.correoUsuario = null;
                this.usuarioActual = null;
                this.permisos = [];
                const code = (error as any)?.code
                const msg = code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request'
                    ? 'Inicio de sesión cancelado.'
                    : code === 'auth/network-request-failed'
                        ? 'Error de red con Firebase. Verifique su conexión.'
                        : `Error de autenticación${code ? ` (${code})` : ''}. Intente de nuevo.`
                return { ok: false, error: msg };
            }
        },

        async restaurarSesion(): Promise<boolean> {
            const token = await getFirebaseIdToken();
            if (!token) return false;
            setAuthToken(token);

            const result = await http.get<Usuario>('/api/me');
            if (!result.ok) {
                await logoutFirebase();
                setAuthToken(null);
                this.autenticado = false;
                this.correoUsuario = null;
                this.usuarioActual = null;
                this.permisos = [];
                return false;
            }

            this.autenticado = true;
            this.correoUsuario = result.data?.email ?? this.correoUsuario;
            this.usuarioActual = result.data ?? null;
            this.permisos = extraerPermisos(result.data ?? null);
            this._registrarHandlersHttp();
            return true;
        },

        async Logout() {
            limpiarHandlersAuth();
            await logoutFirebase();
            setAuthToken(null);
            this.autenticado = false;
            this.correoUsuario = null;
            this.usuarioActual = null;
            this.permisos = [];
        },
    },
});

export default useAutenticar;
